import {
  Accordion,
  Badge,
  Card,
  Divider,
  Group,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconCircleCheck, IconAlertTriangle } from "@tabler/icons-react";
import type { TrizState } from "../api/types";
import { ConclusionPanel } from "./ConclusionPanel";
import { PrincipleBadge } from "./PrincipleBadge";

interface ResultViewProps {
  state: TrizState;
  costUsd?: number;
  durationSec?: number;
}

export function ResultView({ state, costUsd, durationSec }: ResultViewProps) {
  const hasAxes = state.evaluation_axes.length > 0;
  const totalWeight = state.evaluation_axes.reduce((sum, a) => sum + a.weight, 0) || 1;

  const computeWeighted = (axis_scores: Record<string, number> | null): number | null => {
    if (!hasAxes || !axis_scores) return null;
    let ws = 0;
    for (const ax of state.evaluation_axes) ws += ax.weight * (axis_scores[ax.name] ?? 0);
    return ws / totalWeight;
  };

  const ranked = state.solutions
    .map((sol, idx) => {
      const ev = state.evaluations[idx] ?? null;
      const ws = ev ? computeWeighted(ev.axis_scores) : null;
      return { sol, eval: ev, origIdx: idx, weighted: ws };
    })
    .sort((a, b) => {
      if (a.weighted !== null && b.weighted !== null) return b.weighted - a.weighted;
      return (b.eval?.score ?? -1) - (a.eval?.score ?? -1);
    });

  const medals = ["🥇", "🥈", "🥉"];

  return (
    <Stack gap="md">
      <Title order={2}>Результат</Title>

      {state.conclusion && (
        <ConclusionPanel conclusion={state.conclusion} solutions={state.solutions} />
      )}

      <Accordion multiple defaultValue={["solutions"]}>
        {state.components.length > 0 && (
          <Accordion.Item value="components">
            <Accordion.Control>
              <Text fw={600}>🧩 Компоненты системы</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap={6}>
                {state.components.map((c) => (
                  <Text key={c.name}>
                    <Text component="span" fw={600}>
                      {c.name}
                    </Text>{" "}
                    — {c.role}
                  </Text>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {state.technical_contradiction && (
          <Accordion.Item value="tp">
            <Accordion.Control>
              <Text fw={600}>⚖️ Техническое противоречие</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap={4}>
                <Text>
                  <Text component="span" fw={600}>
                    Если:
                  </Text>{" "}
                  {state.technical_contradiction.if_situation}
                </Text>
                <Text>
                  <Text component="span" fw={600}>
                    То:
                  </Text>{" "}
                  {state.technical_contradiction.then_benefit}
                </Text>
                <Text>
                  <Text component="span" fw={600}>
                    Но:
                  </Text>{" "}
                  {state.technical_contradiction.but_cost}
                </Text>
                {state.technical_contradiction.improving_param && (
                  <Group gap="xs" mt="xs">
                    <Badge variant="light" color="green">
                      ↑ №{state.technical_contradiction.improving_param}
                    </Badge>
                    <Badge variant="light" color="red">
                      ↓ №{state.technical_contradiction.worsening_param}
                    </Badge>
                  </Group>
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {state.physical_contradiction && (
          <Accordion.Item value="fp">
            <Accordion.Control>
              <Text fw={600}>⚛️ Физическое противоречие</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap={4}>
                <Text>
                  <Text component="span" fw={600}>
                    Элемент:
                  </Text>{" "}
                  {state.physical_contradiction.element}
                </Text>
                <Text>
                  <Text component="span" fw={600}>
                    Должен быть:
                  </Text>{" "}
                  {state.physical_contradiction.requirement_a}
                </Text>
                <Text size="sm" c="dimmed">
                  причина: {state.physical_contradiction.rationale_a}
                </Text>
                <Text>
                  <Text component="span" fw={600}>
                    И одновременно:
                  </Text>{" "}
                  {state.physical_contradiction.requirement_not_a}
                </Text>
                <Text size="sm" c="dimmed">
                  причина: {state.physical_contradiction.rationale_not_a}
                </Text>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {state.fp_formulation_failed && !state.physical_contradiction && (
          <Accordion.Item value="fp-skipped">
            <Accordion.Control>
              <Group gap="xs">
                <IconAlertTriangle size={16} />
                <Text fw={600}>Физическое противоречие — мягкая деградация</Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Text size="sm" c="dimmed">
                Pipeline не смог однозначно сформулировать физическое противоречие.
                В задаче, вероятно, нет явного ФП или формулировка неоднозначна.
              </Text>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {state.candidate_principles.length > 0 && (
          <Accordion.Item value="candidates">
            <Accordion.Control>
              <Text fw={600}>
                📚 Приёмы-кандидаты ({state.candidate_principles.length})
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="sm">
                {state.candidate_principles.map((c) => (
                  <Card key={c.principle_id} withBorder padding="sm">
                    <Group gap="xs" mb={4}>
                      <PrincipleBadge id={c.principle_id} />
                    </Group>
                    <Text size="sm">{c.rationale}</Text>
                  </Card>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {ranked.length > 0 && (
          <Accordion.Item value="solutions">
            <Accordion.Control>
              <Text fw={600}>💡 Решения и оценки</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                {ranked.map((item, rankPos) => {
                  const medal = medals[rankPos] ?? "  ";
                  return (
                    <Card key={item.origIdx} withBorder padding="md">
                      <Group justify="space-between" align="flex-start" mb="xs">
                        <Group gap="xs">
                          <Text fw={600}>
                            {medal} Решение #{item.origIdx + 1}
                          </Text>
                          {item.eval && (
                            <Badge
                              variant="light"
                              color={
                                item.eval.score >= 0.7
                                  ? "green"
                                  : item.eval.score >= 0.5
                                  ? "yellow"
                                  : "gray"
                              }
                            >
                              ТРИЗ {item.eval.score.toFixed(2)}
                            </Badge>
                          )}
                          {item.weighted !== null && (
                            <Badge variant="filled" color="indigo">
                              взвеш. {item.weighted.toFixed(2)}
                            </Badge>
                          )}
                          {item.sol.principles_used.length > 0 && (
                            <Group gap={4}>
                              <Text size="sm" c="dimmed">
                                приёмы:
                              </Text>
                              {item.sol.principles_used.map((p) => (
                                <PrincipleBadge key={p} id={p} variant="outline" />
                              ))}
                            </Group>
                          )}
                        </Group>
                      </Group>
                      <Text size="sm" mb="xs">
                        {item.sol.description}
                      </Text>
                      {item.eval && (
                        <>
                          <Divider my="xs" />
                          <Text size="sm">
                            <Text component="span" fw={600}>
                              Критик:
                            </Text>{" "}
                            {item.eval.rationale}
                          </Text>
                          {item.eval.suggested_revisions && (
                            <Text size="sm" c="dimmed" mt={4}>
                              <Text component="span" fw={600}>
                                Поправка:
                              </Text>{" "}
                              {item.eval.suggested_revisions}
                            </Text>
                          )}
                          {item.eval.axis_scores && hasAxes && (
                            <Group gap={6} mt="xs">
                              {state.evaluation_axes.map((ax) => {
                                const v = item.eval!.axis_scores?.[ax.name];
                                return (
                                  <Tooltip
                                    key={ax.name}
                                    label={`${ax.name} (вес ${ax.weight})${
                                      ax.description ? ": " + ax.description : ""
                                    }`}
                                    withArrow
                                  >
                                    <Badge variant="outline" color="grape" size="sm">
                                      {ax.name}: {v !== undefined ? v.toFixed(2) : "—"}
                                    </Badge>
                                  </Tooltip>
                                );
                              })}
                            </Group>
                          )}
                        </>
                      )}
                    </Card>
                  );
                })}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}
      </Accordion>

      {(costUsd !== undefined || durationSec !== undefined) && (
        <Group gap="md" mt="xs">
          <Group gap={4}>
            <IconCircleCheck size={16} color="green" />
            <Text size="sm" c="dimmed">
              Стоимость: ${costUsd?.toFixed(3) ?? "—"} · {durationSec?.toFixed(0) ?? "—"}с
            </Text>
          </Group>
        </Group>
      )}
    </Stack>
  );
}
